/**
 * Adding a checked receipt: the commands that put one reviewed scan into a group. Every receipt
 * goes through here the same way, whether it was the only one in a photo, one of several in a
 * photo, or one of several photos picked at once (Miles, 2026-10-29).
 */
import type { Command } from "../commands.ts";
import { guessMcc } from "../mcc.ts";
import type { GroupState } from "../types.ts";
import { receiptTaxBp, type ReceiptDraft } from "./receipt.ts";

/**
 * The label for another receipt at this store on this date: "" when it's the first, else the
 * lowest free number from 2 ("receipt 2", "receipt 3"), since the label is only for showing.
 */
export function nextReceiptLabel(state: GroupState, date: string, store: string): string {
  const name = store.trim();
  const taken = new Set(
    Object.values(state.items)
      .filter((i) => i.date === date && i.store === name)
      .map((i) => i.receipt ?? ""),
  );
  if (!taken.size) return "";
  let n = 2;
  while (taken.has(String(n))) n++;
  return String(n);
}

/** One row as checked on the review screen (money already read into cents). */
export interface CheckedRow {
  itemNo: string;
  name: string;
  /** Null when the price box can't be read: the row is left out. */
  priceCents: number | null;
  couponCents: number;
  crvCents: number;
  taxed: boolean | null;
}

/** A reviewed receipt, ready to add. */
export interface CheckedReceipt {
  /** The reading it came from (for its printed tax and merchant code). */
  draft: ReceiptDraft;
  store: string;
  date: string;
  /** The label typed on the review ("" = pick one: see nextReceiptLabel). */
  receipt: string;
  /** The card it was paid with, "" for none. */
  card: string;
  /** The printed total, null when it's blank. */
  totalCents: number | null;
  /** The rows ticked to add. */
  rows: CheckedRow[];
  /** Who it's split between (person ids). */
  ticked: string[];
}

export interface ReceiptAdd {
  receiptId: string;
  /** The label it's shown with, absent for the first receipt at that store that day. */
  label?: string;
  /** In order: the store's preset (when needed), one item.add per row, then the printed total. */
  commands: Command[];
  /** How many of the commands add an item. */
  items: number;
}

/**
 * The commands that add one checked receipt, kept by its own new id (Miles, 2026-10-28), so
 * renaming its store, date or label later never splits it up. `newId` makes the ids.
 */
export function receiptAddCommands(
  state: GroupState,
  r: CheckedReceipt,
  newId: () => string,
): ReceiptAdd {
  const store = r.store.trim();
  const receiptId = newId();
  const label = r.receipt.trim() || nextReceiptLabel(state, r.date, store) || undefined;
  const commands: Command[] = [];
  // The tax rate this receipt shows (its tax over the taxed rows), so taxed rows are charged what
  // the receipt charged even in a new group with no preset for the store yet.
  const rate = receiptTaxBp(
    r.rows.map((x) => ({
      priceCents: x.priceCents ?? 0,
      couponCents: x.couponCents,
      crvCents: Math.abs(x.crvCents),
      taxed: x.taxed,
    })),
    r.draft.taxCents,
  );
  // A store seen for the first time gets a preset with its merchant code and that rate.
  const preset = state.stores[store];
  const mcc = r.draft.mcc ?? guessMcc(store)?.code;
  if (!preset) {
    commands.push({
      type: "store.set",
      name: store,
      taxBp: rate ?? 0,
      category: "Groceries",
      ...(mcc ? { mcc } : {}),
    });
  } else if (!preset.mcc && mcc) {
    commands.push({
      type: "store.set",
      name: store,
      taxBp: preset.taxBp,
      tipBp: preset.tipBp,
      category: preset.category,
      mcc,
    });
  }
  const storeTax = preset ? preset.taxBp : (rate ?? 0);
  let items = 0;
  for (const x of r.rows) {
    if (x.priceCents === null || !x.name.trim()) continue;
    commands.push({
      type: "item.add",
      itemId: newId(),
      fields: {
        date: r.date,
        store,
        itemNo: x.itemNo.trim(),
        name: x.name.trim(),
        category: null,
        priceCents: x.priceCents,
        couponCents: -Math.abs(x.couponCents),
        crvCents: Math.abs(x.crvCents),
        // Taxed rows use the store's rate, or this receipt's when they differ.
        taxBp:
          x.taxed === false
            ? 0
            : x.taxed === true && rate !== null && rate !== storeTax
              ? rate
              : null,
        paidWith: r.card || null,
        paidBy: null,
        customSplit: "",
        note: "",
        ...(label ? { receipt: label } : {}),
        receiptId,
      },
      ticked: r.ticked,
    });
    items++;
  }
  if (r.totalCents !== null && r.totalCents > 0) {
    commands.push({
      type: "receipt.set",
      date: r.date,
      store,
      receipt: label,
      receiptId,
      cents: r.totalCents,
    });
  }
  return { receiptId, ...(label ? { label } : {}), commands, items };
}
